import React, { useEffect, useState } from "react";
import { ShoppingBag, Loader2, Calendar, Dumbbell } from "lucide-react";
import api from "../lib/api";
import UserNav from "../components/UserNav";
import { useUserAuth } from "../context/UserAuthContext";

const STATUS_COLOR = {
  paid: "text-green-400 border-green-500/30",
  pending: "text-yellow-400 border-yellow-500/30",
  failed: "text-red-400 border-red-500/25",
  expired: "text-white/30 border-white/10",
  initiated: "text-white/35 border-white/10",
};
const STATUS_LABEL = { paid: "Pagado", pending: "Pendiente", failed: "Fallido", expired: "Caducado", initiated: "Iniciado" };

const CAT_COLOR = {
  "Entrenamiento Personal": "text-[#FFCC00]/80 border-[#FFCC00]/25",
  "Plan de Dieta": "text-green-400/80 border-green-500/25",
  "Bono de Sesiones": "text-purple-400/80 border-purple-500/25",
  default: "text-white/40 border-white/10",
};

export default function MisComprasPage() {
  const { user, logout } = useUserAuth();
  const [orders, setOrders] = useState([]);
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/payments/my-orders").then(r => r.data).catch(() => []),
      api.get("/credits/my-credits").then(r => r.data).catch(() => []),
    ]).then(([o, c]) => {
      setOrders(o);
      setCredits(c.map(c => ({ ...c, creditos_restantes: c.creditos_total - c.creditos_usados })));
      setLoading(false);
    });
  }, []);

  const sessionCredits = credits.filter(c => c.creditos_restantes > 0);
  const totalCredits = sessionCredits.reduce((s, c) => s + c.creditos_restantes, 0);

  return (
    <div className="min-h-screen bg-[#050505] text-white font-body" data-testid="mis-compras-page">
      <UserNav user={user} onLogout={logout} />

      <div className="max-w-2xl mx-auto px-4 py-8">
        <p className="text-[10px] tracking-[0.3em] uppercase font-black text-[#FFCC00] mb-1">Portal</p>
        <h1 className="font-display text-3xl mb-7">Mis compras</h1>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#FFCC00]" /></div>
        ) : (
          <>
            {/* Session credits summary */}
            {credits.length > 0 && (
              <div className="mb-8">
                <p className="text-[10px] tracking-[0.25em] uppercase font-black text-white/40 mb-3">Créditos de sesiones</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {credits.map(c => (
                    <div key={c.id} className={`border ${c.creditos_restantes > 0 ? "border-[#FFCC00]/20 bg-[#FFCC00]/3" : "border-white/5 opacity-50"} px-4 py-4`} data-testid={`credit-card-${c.id}`}>
                      <div className="flex items-start gap-3">
                        <Dumbbell size={16} className={c.creditos_restantes > 0 ? "text-[#FFCC00]" : "text-white/20"} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-black text-white truncate">{c.product_nombre}</p>
                          <div className="flex items-baseline gap-2 mt-1">
                            <span className={`font-display text-2xl ${c.creditos_restantes > 0 ? "text-[#FFCC00]" : "text-white/30"}`}>{c.creditos_restantes}</span>
                            <span className="text-xs text-white/35">/ {c.creditos_total} sesiones</span>
                          </div>
                          {/* Progress bar */}
                          <div className="mt-2 h-1 bg-white/8 w-full">
                            <div className="h-1 bg-[#FFCC00]" style={{ width: `${(c.creditos_restantes / c.creditos_total) * 100}%` }} />
                          </div>
                          <p className="text-[9px] text-white/25 mt-1">Comprado: {c.created_at?.slice(0, 10)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {totalCredits > 0 && (
                  <a href="/reservas" data-testid="go-reserve-btn"
                    className="inline-flex items-center gap-2 mt-3 px-5 py-2.5 bg-[#FFCC00] text-black font-black text-[10px] tracking-[0.2em] uppercase hover:bg-white transition-colors">
                    <Calendar size={12} /> Reservar sesión
                  </a>
                )}
              </div>
            )}

            {/* Purchase history */}
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase font-black text-white/40 mb-3">Historial de compras</p>
              {orders.length === 0 ? (
                <div className="border border-white/8 border-dashed p-12 text-center">
                  <ShoppingBag size={32} className="mx-auto text-white/10 mb-3" />
                  <p className="text-white/30 text-sm">Todavía no has realizado ninguna compra.</p>
                  <a href="/tienda" className="inline-block mt-4 text-[10px] font-black tracking-[0.2em] uppercase text-[#FFCC00] hover:text-white transition-colors">
                    Ver servicios disponibles →
                  </a>
                </div>
              ) : (
                <div className="space-y-2" data-testid="orders-list">
                  {orders.map(o => {
                    const s = o.payment_status || "initiated";
                    const catColor = CAT_COLOR[o.product_categoria] || CAT_COLOR.default;
                    return (
                      <div key={o.session_id} className="border border-white/8 bg-[#0a0a0a] px-4 py-3 flex items-center justify-between gap-3" data-testid={`order-row-${o.session_id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {o.product_categoria && (
                              <span className={`text-[9px] font-black tracking-widest uppercase px-1.5 py-0.5 border ${catColor}`}>{o.product_categoria}</span>
                            )}
                            <span className={`text-[9px] font-black tracking-widest uppercase px-1.5 py-0.5 border ${STATUS_COLOR[s] || "text-white/30 border-white/10"}`}>{STATUS_LABEL[s] || s}</span>
                          </div>
                          <p className="text-sm font-bold text-white truncate">{o.product_nombre}</p>
                          <p className="text-[10px] text-white/30 mt-0.5">{o.created_at?.slice(0, 10)}</p>
                        </div>
                        <span className="font-display text-lg text-[#FFCC00] shrink-0">{(o.amount || 0).toFixed(2)} €</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
